import React from 'react';
import { createBrowserRouter, createRoutesFromElements, Navigate, Route } from 'react-router-dom';
import RequireAuth from './components/auth/RequireAuth';
import RequireContractor from './components/auth/RequireContractor';
import DashboardLayout from './components/layouts/DashboardLayout';
import Layout from './components/layouts/PageLayout';
import ReportLayout from './components/layouts/ReportLayout';
import RouteErrorBoundary from "./components/error/RouteErrorBoundary";

//region lazy loaded pages
const NoMatch = React.lazy(() => import('./pages/NoMatchPage'));
const TestRoute = React.lazy(() => import('./pages/TestRoutePage'));
const MediaRecoveryPage = React.lazy(() => import('./pages/MediaRecoveryPage'));
// Auth
const SignInPage = React.lazy(() => import('./pages/Onboarding/SignInPage'));
const SignUpPage = React.lazy(() => import('./pages/Onboarding/SignUpPage'));
const TempPasswordPage = React.lazy(() => import('./pages/Onboarding/TempPasswordPage'));
const SignOutPage = React.lazy(() => import('./pages/Onboarding/SignOutPage'));
const SSOSignInPage = React.lazy(() => import('./pages/Onboarding/SSOSignInPage'));
const MultiFactorAuthPage = React.lazy(() => import('./pages/MultiFactorAuthPage/MultiFactorAuthPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/Onboarding/ForgotPasswordPage'));
// onboarding flow
const OnboardingRedirectPage = React.lazy(() => import('./pages/OnboardingFlow/OnboardingRedirectPage'));
const OnboardingWelcomePage = React.lazy(() => import('./pages/OnboardingFlow/OnboardingWelcomePage/OnboardingWelcomePage'));
const OnboardingFormPage = React.lazy(() => import('./pages/OnboardingFlow/OnboardingFormPage/OnboardingFormPage'));
const OnboardingSetPasswordPage = React.lazy(() => import('./pages/OnboardingFlow/OnboardingSetPasswordPage/OnboardingSetPasswordPage'));
// admin
// const AdminDashboardPage = React.lazy(() => import('./pages/AdminPage/AdminDashboardPage'));
// contractor
const JobListPage = React.lazy(() => import('./pages/ContractorPage/JobListPage'));
const BusinessDetailsPage = React.lazy(() => import('./pages/ContractorPage/BusinessDetailsPage'));
const ManageUsersPage = React.lazy(() => import('./pages/ContractorPage/ManageUsersPage/ManageUsersPage'));
const UserDetailsPage = React.lazy(() => import('./pages/ContractorPage/UserDetailsPage'));
const TipaltiPage = React.lazy(() => import('./pages/ContractorPage/TipaltiPage'));
const JobDetailsPage = React.lazy(() => import('./pages/ContractorPage/JobDetailsPage/JobDetailsPage'));
const AnalyticsPage = React.lazy(() => import("./pages/ContractorPage/AnalyticsPage"));
// reports
const ReportPage = React.lazy(() => import('./pages/ReportPage/ReportPage'));
const PermitsPage = React.lazy(() => import('./pages/PermitsPage/PermitsPage'));
const PhotoSpherePage = React.lazy(() => import('./pages/PhotoSpherePage/PhotoSpherePage'))
//endregion

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<Layout />} ErrorBoundary={RouteErrorBoundary}>
      <Route index element={<SignInPage />} />
      <Route path="sign_in" element={<SignInPage />} />
      <Route path="sign_up" element={<SignUpPage />} />
      <Route path="on_sign_in" element={<SSOSignInPage />} />
      <Route path="sign_out" element={<SignOutPage />} />
      <Route path="multi_factor_authentication" element={<MultiFactorAuthPage />} />

      <Route path="forgot" element={<ForgotPasswordPage />} />
      <Route path="temp-password" element={<TempPasswordPage />} />

      <Route path="onboarding/:contractorType">
        <Route index path="new" element={<OnboardingRedirectPage />} />
        <Route path=":onboardingId" element={<OnboardingWelcomePage />} />
        <Route path=":onboardingId/form" element={<OnboardingFormPage />} />
        <Route path=":onboardingId/set_password" element={<OnboardingSetPasswordPage />} />
      </Route>

      <Route
        path="dashboard"
        element={
          <RequireContractor>
            <DashboardLayout />
          </RequireContractor>
        }
      >
        <Route index element={<JobListPage />} />
        <Route path="job/:orderToken" element={<JobDetailsPage />} />
        <Route path="business" element={<BusinessDetailsPage />} />

        <Route path="payments">
          <Route index path="setup" element={<TipaltiPage iframeType="setup_process" />} />
          <Route path="history" element={<TipaltiPage iframeType="payment_history" />} />
          <Route path="invoices" element={<TipaltiPage iframeType="invoice_history" />} />
        </Route>

        <Route path="analytics" element={<AnalyticsPage />} />

        <Route path="manage">
          <Route index element={<ManageUsersPage />} />
          <Route path="user/:userId" element={<UserDetailsPage />} />
        </Route>
      </Route>

      <Route path="orders" element={<ReportLayout />}>
        <Route path=":orderToken/reports" element={<ReportPage />} />
        <Route path=":orderToken/reports/:reportId" element={<ReportPage />} />
        <Route path=":orderToken/reports/:reportId/print" element={<ReportPage />} />
        <Route path=":orderToken/denver_inspection_pdf" element={<Navigate to=":orderToken/reports" />} />
        <Route path=":orderToken/permits" element={<PermitsPage />} />
      </Route>

      {/* Without the ReportLayout */}
      <Route path="orders">
        <Route path=":orderToken/reports/:reportId/photo_spheres" element={<PhotoSpherePage />} />
        <Route path=":orderToken/reports/:reportId/photo_spheres/:photoSphereId" element={<PhotoSpherePage />} />
      </Route>

      <Route path="rehab" element={<DashboardLayout />}>
        <Route
          index
          element={(
            <RequireAuth>
              <div>
                <h1>Rehab Index</h1>
              </div>
            </RequireAuth>
          )}
        />
        <Route
          path=":projectId"
          element={(
            <RequireAuth>
              <div>
                <h1>Rehab Project</h1>
              </div>
            </RequireAuth>
          )}
        />
        <Route
          path=":projectId/print"
          element={(
            <RequireAuth>
              <div>
                <h1>Rehab Project Print</h1>
              </div>
            </RequireAuth>
          )}
        />
      </Route>

      <Route path="/media_recovery" element={<MediaRecoveryPage />} />
      <Route path="/test" element={<TestRoute />} />
      <Route path="*" element={<NoMatch />} />
    </Route>,
  ), {
    future: {
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_relativeSplatPath: true,
      v7_skipActionErrorRevalidation: true,
    }
  }
);
export default router;
